/**
 * @file All the general utility that is hard to classify goes here.
 */

/**
 * Recursively makes all optional fields required in a type.
 *
 * @example
 * interface User {
 *   name?: string;
 *   age?: number;
 *   profile?: {
 *     bio?: string;
 *     avatar?: string;
 *   };
 * }
 *
 * type RequiredUser = DeepRequired<User>;
 * // Result: {
 * //   name: string;
 * //   age: number;
 * //   profile: {
 * //     bio: string;
 * //     avatar: string;
 * //   };
 * // }
 */
export type DeepRequired<T> = T extends object
  ? T extends Array<infer U>
    ? Array<DeepRequired<U>> // Handle arrays
    : T extends Date | RegExp | ((...args: any[]) => any)
      ? T // Don't process special objects
      : {
          [K in keyof T]-?: DeepRequired<T[K]>; // Remove optional modifier and recurse
        }
  : T; // Primitives remain unchanged

export type DeepPartial<T> = T extends object
  ? T extends Array<infer U>
    ? Array<DeepPartial<U>> // Handle arrays
    : T extends Date | RegExp | ((...args: any[]) => any)
      ? T // Don't process special objects
      : {
          [K in keyof T]?: DeepPartial<T[K]>; // Make all properties optional and recurse
        }
  : T; // Primitives remain unchanged

export function padTwoDigits(num: number): string {
  return num.toString().padStart(2, '0');
}

/**
 * Removes properties starting with '_' from an object.
 * @returns
 */
export function removeHideProperties(obj: object): object {
  const copy = { ...obj };
  Object.keys(copy).forEach((key) => {
    if (key.startsWith('_')) {
      delete copy[key];
    }
  });
  return copy;
}

/**
 * Converts a Blob or File to a Data URL.
 */
export function blobToDataURL(blob: Blob | File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function bytesToSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
}

export function noop() {}

export function hashStr(src) {
  if (!src) return '0';

  let hash = 5381; // Use DJB2 hash algorithm starting value
  const len = src.length;

  // Optimized loop with fewer operations
  for (let i = 0; i < len; i++) {
    hash = (hash * 33) ^ src.charCodeAt(i);
  }

  // Ensure positive number and convert to base-36
  return (hash >>> 0).toString(36);
}

export function copy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function sleep(time: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, time);
  });
}

/**
 * It does nothing at runtime, but tells TypeScript to treat `val` as type `T`.
 * @param _val
 */
export function toType<T>(_val: unknown): asserts _val is T {}

export function upperFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function lowerFirst(str: string): string {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export function equals(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!equals(a[i], b[i])) return false;
    }
    return true;
  }

  if (typeof a === 'object' && a != null && b != null) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!equals(a[key], b[key])) return false;
    }
    return true;
  }

  return false;
}

export function filterObject<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null)
  ) as Partial<T>;
}

export function safelyParseJSON(content: string) {
  try {
    return JSON.parse(content);
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Checks if a value is a plain object (not an array, null, or other special objects)
 */
function isPlainObject(value: unknown): value is Record<string, any> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
}

/**
 * Deep merge multiple objects into target.
 * Later source objects have higher priority and will overwrite earlier ones.
 *
 * @param target - The target object to merge into
 * @param sources - Any number of source objects to merge from
 * @returns The merged object (same reference as target)
 *
 * @example
 * const target = { a: 1, b: { x: 1, y: 2 } };
 * const source1 = { b: { y: 3, z: 4 }, c: 5 };
 * const source2 = { b: { x: 10 } };
 * deepMerge(target, source1, source2);
 * // Result: { a: 1, b: { x: 10, y: 3, z: 4 }, c: 5 }
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: DeepPartial<T>[]
): T {
  if (!sources.length) return target;

  for (const source of sources) {
    if (!isPlainObject(source)) continue;

    for (const key in source) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) continue;

      const sourceValue = source[key];
      const targetValue = target[key];

      // If both are plain objects, merge them recursively
      if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
        target[key] = deepMerge({ ...targetValue }, sourceValue as any);
      } else if (sourceValue !== undefined) {
        // Otherwise, overwrite with source value (later sources have priority)
        target[key] = sourceValue as any;
      }
    }
  }

  return target;
}
